"use client"

import { Head, router } from "@inertiajs/react"
import { ArrowLeft, Inbox01, MessageChatSquare } from "@untitledui/icons"
import { useState } from "react"

import { SuggestionCard } from "@/components/admin/collabs/suggestion-card"
import { ButtonGroup, ButtonGroupItem } from "@/components/ui/button-group"
import { EmptyState } from "@/components/ui/empty-state"
import { Link } from "@/components/ui/link"
import { Toggle } from "@/components/ui/toggle"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import {
  adminAcceptSuggestionPath,
  adminCollabPath,
  adminCollabSuggestionsPath,
  adminRejectSuggestionPath,
} from "@/routes"
import { CollabSuggestionStatusEnum as SuggestionStatus } from "@/types/contents"
import type { CollabSuggestion } from "@/types/contents/collab_suggestion"
import type { Brief, TrackPrompt } from "@/types/serializers"

/*
 * Typelizer can't read Alba block attributes, so `TrackSuggestion.ts`
 * ships `contributor_name`, `post`, and `status` as `unknown`. Narrowed
 * here against `TrackSuggestionResource`'s block attributes (see that
 * file) rather than left loose — same move as `CollabShowBrief` in
 * `collabs/show.tsx`.
 */
type PromptSummary = Pick<TrackPrompt, "id" | "title" | "position">
type visitFilterStatusType = SuggestionStatus | "all"

const ALL_STATUS = "all"
const FILTER_ITEMS: { id: visitFilterStatusType; label: string }[] = [
  { id: ALL_STATUS, label: "All" },
  { id: SuggestionStatus.pending, label: "Pending" },
  { id: SuggestionStatus.accepted, label: "Accepted" },
  { id: SuggestionStatus.rejected, label: "Rejected" },
]
interface SuggestionGroup {
  track_prompt: PromptSummary
  suggestions: CollabSuggestion[]
}

interface SuggestionsPageProps {
  brief: Pick<Brief["brief"], "slug" | "title" | "suggestions_count">
  suggestions_by_prompt: SuggestionGroup[]
  status_filter: SuggestionStatus | null
}

export default function AdminCollabSuggestions({
  brief,
  suggestions_by_prompt: suggestionsByPrompt,
  status_filter: statusFilter,
}: SuggestionsPageProps) {
  const [isShowPromptWithNoSuggestion, setIsShowPromptWithNoSuggestion] =
    useState(true)
  const visitFilter = (status: visitFilterStatusType) => {
    router.get(
      adminCollabSuggestionsPath(brief.slug),
      status === ALL_STATUS ? {} : { status },
      { preserveState: true, replace: true },
    )
  }

  // Both actions redirect back here — Inertia re-fetches the grouped
  // props from the server, so no client-side status mutation.
  const acceptSuggestion = (id: number) => {
    router.patch(adminAcceptSuggestionPath(id), {}, { preserveScroll: true })
  }

  const rejectSuggestion = (id: number) => {
    router.patch(adminRejectSuggestionPath(id), {}, { preserveScroll: true })
  }

  const hasAnySuggestions = brief.suggestions_count > 0
  const hasVisibleSuggestions = suggestionsByPrompt.some(
    (group) => group.suggestions.length > 0,
  )

  return (
    <DashboardLayout>
      <Head title={`${brief.title} — Suggestions`} />
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pt-8 pb-12 lg:px-8">
        <Link
          href={adminCollabPath(brief.slug)}
          className="text-tertiary hover:text-secondary flex items-center gap-1.5 text-sm font-medium"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          {brief.title}
        </Link>

        <header className="flex flex-col gap-0.5">
          <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
            Suggestions
          </h1>
          <p className="text-md text-tertiary">
            Review what the community submitted and accept what belongs in the
            playlist.
          </p>
        </header>

        {/* Filter tools */}
        <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <ButtonGroup
            size="sm"
            disallowEmptySelection
            className="w-full shadow-none sm:w-max"
            selectedKeys={[statusFilter ?? ALL_STATUS]}
            onSelectionChange={(keys) => {
              const [selected] = [...keys]
              if (selected) visitFilter(selected as visitFilterStatusType)
            }}
          >
            {FILTER_ITEMS.map((item) => (
              <ButtonGroupItem key={item.id} id={item.id}>
                {item.label}
              </ButtonGroupItem>
            ))}
          </ButtonGroup>
          <Toggle
            size="sm"
            className="w-full sm:w-max"
            label="Show empty slots"
            isSelected={isShowPromptWithNoSuggestion}
            onChange={setIsShowPromptWithNoSuggestion}
          />
        </section>

        {/* Prompts * Suggestions */}
        {!hasAnySuggestions ? (
          <section>
            <EmptySuggestionFallback />
          </section>
        ) : !hasVisibleSuggestions ? (
          <section>
            <EmptySuggestionByFilterFallback />
          </section>
        ) : (
          <section>
            <SuggestionPrompt
              suggestionsByPrompt={suggestionsByPrompt}
              showEmptyPrompts={isShowPromptWithNoSuggestion}
              onAccept={acceptSuggestion}
              onReject={rejectSuggestion}
            />
          </section>
        )}
      </main>
    </DashboardLayout>
  )
}

/**
 * Suggestions prompt component
 */
function SuggestionPrompt({
  suggestionsByPrompt,
  showEmptyPrompts,
  onAccept,
  onReject,
}: {
  suggestionsByPrompt: SuggestionGroup[]
  showEmptyPrompts: boolean
  onAccept: (id: number) => void
  onReject: (id: number) => void
}) {
  return (
    <div className="flex flex-col gap-8">
      {suggestionsByPrompt.map((group) => {
        const hasSuggestions = group.suggestions.length > 0
        if (!showEmptyPrompts && !hasSuggestions) return null

        return (
          <section
            key={group.track_prompt.id}
            aria-label={group.track_prompt.title}
            className="bg-surface-grouped mt-2 flex flex-col gap-3 rounded-xl p-2"
          >
            <h2 className="text-primary text-sm font-semibold">
              {group.track_prompt.title}
            </h2>

            {hasSuggestions ? (
              <div className="flex flex-col gap-3">
                {group.suggestions.map((suggestion) => (
                  <SuggestionCard
                    key={suggestion.id}
                    suggestion={suggestion}
                    onAccept={onAccept}
                    onReject={onReject}
                  />
                ))}
              </div>
            ) : (
              <div className="border-disabled flex min-h-50 items-center justify-center rounded-xl border border-dashed p-4">
                <p className="text-tertiary text-md">
                  No suggestions yet for this slot.
                </p>
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

/**
 * Empty suggestion fallback component
 */
const EmptySuggestionFallback = () => (
  <EmptyState size="sm" className="py-10">
    <EmptyState.Header pattern="none">
      <EmptyState.FeaturedIcon
        color="gray"
        theme="modern"
        icon={MessageChatSquare}
      />
    </EmptyState.Header>
    <EmptyState.Content>
      <p className="text-primary text-md text-center font-semibold">
        No suggestions yet
      </p>
      <EmptyState.Description>
        Suggestions show up here as visitors and guests submit tracks for this
        collab.
      </EmptyState.Description>
    </EmptyState.Content>
  </EmptyState>
)

/*
 * Empty suggestion fallback component by filter
 */
const EmptySuggestionByFilterFallback = () => (
  <EmptyState size="sm" className="py-10">
    <EmptyState.Header pattern="none">
      <EmptyState.FeaturedIcon color="gray" theme="modern" icon={Inbox01} />
    </EmptyState.Header>
    <EmptyState.Content>
      <p className="text-primary text-md text-center font-semibold">
        No suggestions match this filter
      </p>
      <EmptyState.Description>
        Try a different status, or clear the filter to see everything.
      </EmptyState.Description>
    </EmptyState.Content>
  </EmptyState>
)
